import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { translate } from "../../store/actions.js";
import "./OrionOs.css";

const data = {
  en: {
    back: "Back to Portfolio",
    title: "Orion OS — What we've really built with AI",
    quote1: "The operating system for AI-powered development teams.",
    quote2:
      "v2.0.0 — Public system layer, multi-framework agents, kanban per project, decision prefix convention.",
    whatIsTitle: "What is Orion OS?",
    whatIsText1:
      "Orion OS is a framework for building and orchestrating AI agent teams that develop real software. It's not a chatbot — it's a working methodology with roles, rules, memory, intelligent agent management, and multi-project support.",
    whatIsText2:
      "The team operates like a development agency: each agent has a specialized role, follows clear rules, and communicates through GitHub Issues as the single state layer.",
    hierarchyTitle: "The hierarchy",
    hierarchyCode: `Director / Founder (vision & business decisions)
  └── Orion (CTO / permanent orchestrator)
        └── Per project:
              ├── Backend Tech Lead (Nestor)
              │     └── Subagents: architecture, migrations, security
              ├── Frontend Tech Lead (Olga)
              │     ├── OLGA.md       → Angular stack
              │     ├── OLGA-REACT.md → React stack
              │     └── Subagents: components, performance, accessibility
              └── QA Agent (Bruno)
                    └── Automated CI — runs on every PR`,
    commandsTitle: "Session commands",
    commands: [
      {
        cmd: '"Orion iniciar proyecto"',
        desc: "Wizard → choose stack → create repo + files + issue #1",
      },
      {
        cmd: '"despierta Orion, vamos con <project>"',
        desc: "Smart bootstrap → load context + skills for that project",
      },
      {
        cmd: '"Orion cierra sesión"',
        desc: "Update docs + log session + confirm next priority",
      },
    ],
    commandsRef: "Full reference: workflows/commands.md",
    coreConceptsTitle: "Core concepts",
    coreConcepts: [
      {
        name: "Stack-scoped agent identities",
        desc: "Olga has two identities: OLGA.md for Angular projects and OLGA-REACT.md for React projects. The CLAUDE.md of each repo tells her which to load. One repo, one agent, one session — never mixed.",
      },
      {
        name: "Smart session init",
        desc: "When waking up for a project, Orion loads the exact skills for that project's stack. React → react-patterns. Angular → angular-patterns. Never mixed.",
      },
      {
        name: "Kanban per project (D96)",
        desc: "Each active project has its own GitHub Project board at account level. Standard columns: Backlog → Ready → In progress → In review → Done. Orion references issues by repo, not by board URL (D97).",
      },
      {
        name: "Decision prefix convention (D99)",
        desc: "Project-specific decisions use a prefix: GN-D (GameOn), N-D (NutriApp), PM-D (PortfolioMV). Universal decisions use bare D.",
      },
      {
        name: "Reversible decisions (D89)",
        desc: "Every technical decision must be reversible or explicitly documented as irreversible.",
      },
      {
        name: "Scaffold via GitHub MCP (D90)",
        desc: "Orion creates project scaffolds by pushing files to the repo via GitHub MCP. Mario clones and runs npm install locally.",
      },
      {
        name: "Multi-project architecture",
        desc: "Each project has isolated context, decisions, skills, repos, and kanban. Max 2 issues In Progress per project simultaneously (D7).",
      },
      {
        name: "Issue-driven development",
        desc: "GitHub Issues are the only communication channel between Orion and execution agents. Format: Context + Done + Agent Prompt + Skills + Test Plan.",
      },
      {
        name: "Decision registry",
        desc: "Universal decisions in DECISIONS.md. Project-specific in projects/<n>-decisions.md.",
      },
    ],
    repoTitle: "Repository structure",
    repoCode: `orion/
  ├── README.md                  → This file
  ├── ORION.md                   → Orion's identity, rules, session protocol
  ├── DECISIONS.md               → Universal decisions (all projects)
  │
  ├── agents/
  │   ├── AGENT_RULES.md         → Universal rules for all agents
  │   ├── DIRECTOR.md            → Founder profile
  │   ├── NESTOR.md              → Backend tech lead (NestJS)
  │   ├── OLGA.md                → Frontend tech lead (Angular — GameOn)
  │   ├── OLGA-REACT.md          → Frontend tech lead (React — PortfolioMV)
  │   ├── BRUNO.md               → QA agent (CI-based)
  │   └── subagents/
  │       ├── angular-component-architecture.md
  │       ├── angular-performance.md
  │       ├── angular-accessibility.md
  │       ├── react-component-architecture.md
  │       ├── ui-design-reviewer.md
  │       ├── nestjs-architecture.md
  │       ├── typeorm-migrations.md
  │       └── backend-security.md
  │
  ├── projects/
  │   ├── gameon.md              → GameOn (Angular + NestJS — polyrepo)
  │   ├── gameon-decisions.md
  │   ├── gameon-ideas.md
  │   ├── nutriapp.md            → NutriApp (React + Supabase — monorepo)
  │   ├── nutriapp-decisions.md
  │   ├── portfoliomv.md         → PortfolioMV (React CRA — single repo)
  │   └── portfoliomv-decisions.md
  │
  ├── logs/
  │   ├── sessions.jsonl
  │   ├── agent-feedback.jsonl
  │   └── post-mortems.jsonl
  │
  ├── rfcs/                      → Pending architecture decisions
  │
  ├── skills/
  │   ├── universal/
  │   ├── backend/
  │   └── frontend/
  │       ├── angular-patterns.md
  │       ├── angular-signals.md
  │       ├── api-service.md
  │       └── react-patterns.md
  │
  ├── templates/
  └── workflows/
      ├── commands.md            → Session + agent bootstrap protocol
      ├── project-init.md
      ├── context-switch.md
      ├── health-check.md
      ├── issue-quality.md
      ├── skill-injection.md
      ├── agent-feedback.md
      ├── post-mortem.md
      └── security-incident.md`,
    activeProjectsTitle: "Active projects",
    activeProjects: [
      {
        name: "GameOn",
        stack: "Angular 21 + NestJS + PostgreSQL",
        top: "Polyrepo",
        repo: "gameon-api",
        status: "v1.5.2 production",
      },
      {
        name: "NutriApp",
        stack: "React 19 + Vite + Supabase",
        top: "Monorepo",
        repo: "nutriapp",
        status: "Phase 1 complete",
      },
      {
        name: "PortfolioMV",
        stack: "React 18 + CRA",
        top: "Single repo",
        repo: "portfolioMV",
        status: "Active — features phase",
      },
    ],
    roadmapTitle: "Roadmap",
    roadmapCode: `v1.0.0  ✅  Foundation — manual framework, single project
v1.1.0  ✅  Separation of Concerns — framework decoupled from project
v1.2.0  ✅  Metrics & Observability — dashboard, logs, health checks
v1.3.0  ✅  Agent Intelligence — quality scoring, skill injection, feedback
v1.4.0  ✅  Multi-Project Ready — templates, init workflow, context switching
v1.5.0  ✅  Multi-Framework + Session Commands + Reversible Architecture
v2.0.0  ✅  Public system layer (orion-os), OLGA-REACT, kanban per project,
             agent bootstrap protocol, decision prefix convention (D96-D99)
v3.0.0  ⬜  Orion OS web interface — wizard, GitHub OAuth, zero config`,
    paradigmTitle: "The central paradigm: AI as architecture, not autocomplete",
    paradigmText1:
      "Most developers use AI to generate code faster. We've built something different: an operating system for AI work, where AI doesn't execute isolated tasks, but operates within a framework with memory, roles, protocols, and persistent decisions.",
    paradigmText2: "That completely changes the kind of work you can do alone.",
    layersTitle: "What we have built",
    layersText1:
      "Orion OS is an AI agent orchestration framework designed for a single developer to operate with the capacity of a team.",
    layersText2: "The system has three layers:",
    layer1Title: "1. Persistent memory across sessions",
    layer1Text:
      'Claude has no native memory. We solved this with live files in GitHub: ORION.md, DECISIONS.md, and per-project files. Each session begins by reading these files via GitHub MCP. Orion "remembers" because context is built explicitly, not assumed. This allowed us to maintain architectural coherence across 33+ sessions and months of development.',
    layer2Title: "2. Specialized agents with defined roles",
    layer2List: [
      "Nestor — Backend Tech Lead (VSCode + Copilot Pro + GitHub MCP). Operates in NestJS.",
      "Olga — Frontend Tech Lead Angular/React (Antigravity + GitHub MCP). Atomic Design, Signals, TailwindCSS.",
      "Bruno — QA Agent (GitHub Actions CI). Automated, no human intervention.",
    ],
    layer2Text:
      "Each agent receives context via CLAUDE.md in their repo, reads instructions via GitHub MCP, waits for an issue number, and executes within explicit guardrails. They don't improvise — they operate within a system.",
    layer3Title: "3. GitHub MCP as the nervous system",
    layer3Text:
      "The GitHub MCP connected to Claude makes everything real, not just conversation. Orion can read files, create issues, push code, manage PRs — all from Claude's session. There's no manual copy-pasting between the model and the repo. System state lives in GitHub; decisions live in GitHub; the agents' work flows in and out of GitHub.",
    toolsTitle: "The AI tools we use and what for",
    tools: [
      {
        name: "Claude.ai Pro (Sonnet/Opus)",
        role: "Orion — architecture, decisions, coordination",
      },
      {
        name: "GitHub Copilot Pro (VSCode)",
        role: "Nestor — day-to-day backend implementation",
      },
      {
        name: "Antigravity",
        role: "Olga — frontend implementation, Angular/React",
      },
      {
        name: "Gemini Pro/Flash",
        role: "Mechanical tasks, refactoring, quick analysis",
      },
      {
        name: "GitHub Actions",
        role: "Bruno — CI/CD, SonarCloud, automated QA",
      },
      {
        name: "SonarCloud",
        role: "Code quality — 3 layers (IDE → CI → deploy)",
      },
      { name: "GitHub MCP", role: "Connects Claude with repos in real-time" },
      { name: "Vercel MCP", role: "Coordinated deployment from session" },
    ],
    toolsDesc:
      "The model selection principle is documented as a decision (D66): light model for mechanical tasks, heavy model for architecture. Never the reverse — because the cost is in the context, not the token.",
    discoveriesTitle: "What we've discovered about working with AI",
    discoveries: [
      {
        name: "Context is the product.",
        text: "A model without structured context is a generic assistant. The same model with ORION.md + DECISIONS.md + gameon.md is a technical CTO that remembers 33 sessions of decisions and can pick up exactly where we left off.",
      },
      {
        name: "Agents need guardrails, not freedom.",
        text: "The most important discovery: AI agents execute better when they have explicit constraints. Nestor doesn't \"create issues\" — only Orion does that (D5). Olga doesn't mix Tailwind with hardcoded values — it's in the agent's rules. When you reduce the agent's decision space, output quality rises.",
      },
      {
        name: "Issue quality determines output quality.",
        text: "We developed a quality score system for issues (≥8/10 before assigning to an agent). A poorly written issue produces code that has to be redone. A well-structured issue — context + agent prompt + test plan — produces code that passes CI on the first try.",
      },
      {
        name: "MCPs change the type of work possible.",
        text: "Without MCP, Claude is a text model generating code to be copied. With GitHub MCP, Claude can read the real repo state, create issues, push solutions, and open PRs — without you touching the keyboard for those operations. It's a qualitative leap, not quantitative.",
      },
      {
        name: "System/instance separation scales knowledge.",
        text: "In v2.0.0 we extracted the generic system to a public repo (orion-os) and kept the instance private (orion). This allows the framework to be reusable while project-specific knowledge remains private. It's the same logic as an open-source framework with private projects on top.",
      },
    ],
    productionTitle: "What we have produced with this system",
    productionText1:
      "With this architecture, a single developer took GameOn from zero to production in ~33 sessions: NestJS + PostgreSQL + JWT + Google OAuth backend, Angular 21 standalone + Signals + Tailwind frontend, CI/CD with GitHub Actions + SonarCloud quality gates, Vercel deployment with separate staging, organizations system, tournaments, public profiles, role system, and automated migrations pipeline.",
    productionText2:
      "A second project (NutriApp — React + Supabase) runs in parallel with the same operating system, without polluting the GameOn context.",
    productionHighlight:
      "That is Orion OS. It's not a set of prompts — it's a way of working with AI that scales with product complexity.",
  },
  es: {
    back: "Volver al Portafolio",
    title: "Orion OS — Lo que realmente hemos construido con IA",
    quote1:
      "El sistema operativo para equipos de desarrollo potenciados por IA.",
    quote2:
      "v2.0.0 — Capa de sistema pública, agentes multi-framework, kanban por proyecto, convención de prefijos para decisiones.",
    whatIsTitle: "¿Qué es Orion OS?",
    whatIsText1:
      "Orion OS es un framework para construir y orquestar equipos de agentes IA que desarrollan software real. No es un chatbot — es una metodología de trabajo con roles, reglas, memoria, gestión inteligente de agentes y soporte multiproyecto.",
    whatIsText2:
      "El equipo opera como una agencia de desarrollo: cada agente tiene un rol especializado, sigue reglas claras y se comunica a través de GitHub Issues como única capa de estado.",
    hierarchyTitle: "La jerarquía",
    hierarchyCode: `Director / Founder (vision & business decisions)
  └── Orion (CTO / permanent orchestrator)
        └── Per project:
              ├── Backend Tech Lead (Nestor)
              │     └── Subagents: architecture, migrations, security
              ├── Frontend Tech Lead (Olga)
              │     ├── OLGA.md       → Angular stack
              │     ├── OLGA-REACT.md → React stack
              │     └── Subagents: components, performance, accessibility
              └── QA Agent (Bruno)
                    └── Automated CI — runs on every PR`,
    commandsTitle: "Comandos de sesión",
    commands: [
      {
        cmd: '"Orion iniciar proyecto"',
        desc: "Wizard → elegir stack → crear repo + archivos + issue #1",
      },
      {
        cmd: '"despierta Orion, vamos con <project>"',
        desc: "Bootstrap inteligente → cargar contexto + habilidades para ese proyecto",
      },
      {
        cmd: '"Orion cierra sesión"',
        desc: "Actualizar docs + registrar sesión + confirmar siguiente prioridad",
      },
    ],
    commandsRef: "Referencia completa: workflows/commands.md",
    coreConceptsTitle: "Conceptos clave",
    coreConcepts: [
      {
        name: "Identidades de agente por stack",
        desc: "Olga tiene dos identidades: OLGA.md para proyectos Angular y OLGA-REACT.md para proyectos React. El CLAUDE.md de cada repo le dice cuál cargar. Un repo, un agente, una sesión — nunca se mezclan.",
      },
      {
        name: "Inicio de sesión inteligente",
        desc: "Al despertar para un proyecto, Orion carga las habilidades exactas para el stack de ese proyecto. React → react-patterns. Angular → angular-patterns. Nunca se mezclan.",
      },
      {
        name: "Kanban por proyecto (D96)",
        desc: "Cada proyecto activo tiene su propio tablero de GitHub Projects a nivel de cuenta. Columnas estándar: Backlog → Ready → In progress → In review → Done. Orion referencia issues por repo, no por URL del tablero (D97).",
      },
      {
        name: "Convención de prefijos de decisión (D99)",
        desc: "Las decisiones específicas de un proyecto usan un prefijo: GN-D (GameOn), N-D (NutriApp), PM-D (PortfolioMV). Las decisiones universales usan solo D.",
      },
      {
        name: "Decisiones reversibles (D89)",
        desc: "Cada decisión técnica debe ser reversible o documentada explícitamente como irreversible.",
      },
      {
        name: "Scaffold vía GitHub MCP (D90)",
        desc: "Orion crea scaffolds de proyectos haciendo push de archivos al repo vía GitHub MCP. Mario clona y ejecuta npm install localmente.",
      },
      {
        name: "Arquitectura multiproyecto",
        desc: "Cada proyecto tiene contexto, decisiones, habilidades, repos y kanban aislados. Máx 2 issues En Progreso por proyecto simultáneamente (D7).",
      },
      {
        name: "Desarrollo guiado por Issues",
        desc: "GitHub Issues son el único canal de comunicación entre Orion y los agentes de ejecución. Formato: Contexto + Done + Prompt del agente + Skills + Test Plan.",
      },
      {
        name: "Registro de decisiones",
        desc: "Decisiones universales en DECISIONS.md. Específicas de proyecto en projects/<n>-decisions.md.",
      },
    ],
    repoTitle: "Estructura del repositorio",
    repoCode: `orion/
  ├── README.md                  → This file
  ├── ORION.md                   → Orion's identity, rules, session protocol
  ├── DECISIONS.md               → Universal decisions (all projects)
  │
  ├── agents/
  │   ├── AGENT_RULES.md         → Universal rules for all agents
  │   ├── DIRECTOR.md            → Founder profile
  │   ├── NESTOR.md              → Backend tech lead (NestJS)
  │   ├── OLGA.md                → Frontend tech lead (Angular — GameOn)
  │   ├── OLGA-REACT.md          → Frontend tech lead (React — PortfolioMV)
  │   ├── BRUNO.md               → QA agent (CI-based)
  │   └── subagents/
  │       ├── angular-component-architecture.md
  │       ├── angular-performance.md
  │       ├── angular-accessibility.md
  │       ├── react-component-architecture.md
  │       ├── ui-design-reviewer.md
  │       ├── nestjs-architecture.md
  │       ├── typeorm-migrations.md
  │       └── backend-security.md
  │
  ├── projects/
  │   ├── gameon.md              → GameOn (Angular + NestJS — polyrepo)
  │   ├── gameon-decisions.md
  │   ├── gameon-ideas.md
  │   ├── nutriapp.md            → NutriApp (React + Supabase — monorepo)
  │   ├── nutriapp-decisions.md
  │   ├── portfoliomv.md         → PortfolioMV (React CRA — single repo)
  │   └── portfoliomv-decisions.md
  │
  ├── logs/
  │   ├── sessions.jsonl
  │   ├── agent-feedback.jsonl
  │   └── post-mortems.jsonl
  │
  ├── rfcs/                      → Pending architecture decisions
  │
  ├── skills/
  │   ├── universal/
  │   ├── backend/
  │   └── frontend/
  │       ├── angular-patterns.md
  │       ├── angular-signals.md
  │       ├── api-service.md
  │       └── react-patterns.md
  │
  ├── templates/
  └── workflows/
      ├── commands.md            → Session + agent bootstrap protocol
      ├── project-init.md
      ├── context-switch.md
      ├── health-check.md
      ├── issue-quality.md
      ├── skill-injection.md
      ├── agent-feedback.md
      ├── post-mortem.md
      └── security-incident.md`,
    activeProjectsTitle: "Proyectos activos",
    activeProjects: [
      {
        name: "GameOn",
        stack: "Angular 21 + NestJS + PostgreSQL",
        top: "Polyrepo",
        repo: "gameon-api",
        status: "v1.5.2 production",
      },
      {
        name: "NutriApp",
        stack: "React 19 + Vite + Supabase",
        top: "Monorepo",
        repo: "nutriapp",
        status: "Fase 1 completa",
      },
      {
        name: "PortfolioMV",
        stack: "React 18 + CRA",
        top: "Repo único",
        repo: "portfolioMV",
        status: "Activo — fase features",
      },
    ],
    roadmapTitle: "Hoja de ruta",
    roadmapCode: `v1.0.0  ✅  Foundation — manual framework, single project
v1.1.0  ✅  Separation of Concerns — framework decoupled from project
v1.2.0  ✅  Metrics & Observability — dashboard, logs, health checks
v1.3.0  ✅  Agent Intelligence — quality scoring, skill injection, feedback
v1.4.0  ✅  Multi-Project Ready — templates, init workflow, context switching
v1.5.0  ✅  Multi-Framework + Session Commands + Reversible Architecture
v2.0.0  ✅  Public system layer (orion-os), OLGA-REACT, kanban per project,
             agent bootstrap protocol, decision prefix convention (D96-D99)
v3.0.0  ⬜  Orion OS web interface — wizard, GitHub OAuth, zero config`,
    paradigmTitle:
      "El paradigma central: IA como arquitectura, no como autocompletado",
    paradigmText1:
      "La mayoría de desarrolladores usan IA para generar código más rápido. Nosotros hemos construido algo distinto: un sistema operativo para el trabajo con IA, donde la IA no ejecuta tareas aisladas, sino que opera dentro de un framework con memoria, roles, protocolos y decisiones persistentes.",
    paradigmText2:
      "Eso cambia completamente el tipo de trabajo que puedes hacer solo.",
    layersTitle: "Lo que hemos construido",
    layersText1:
      "Orion OS es un framework de orquestación de agentes IA diseñado para que un solo desarrollador opere con la capacidad de un equipo.",
    layersText2: "El sistema tiene tres capas:",
    layer1Title: "1. Memoria persistente entre sesiones",
    layer1Text:
      'Claude no tiene memoria nativa. Lo resolvimos con archivos vivos en GitHub: ORION.md, DECISIONS.md, y archivos por proyecto. Cada sesión empieza leyendo esos archivos vía GitHub MCP. Orion "recuerda" porque el contexto se construye explícitamente, no se asume. Esto nos permitió mantener coherencia arquitectónica a través de 33+ sesiones y meses de desarrollo.',
    layer2Title: "2. Agentes especializados con roles definidos",
    layer2List: [
      "Nestor — Backend Tech Lead (VSCode + Copilot Pro + GitHub MCP). Opera en NestJS.",
      "Olga — Frontend Tech Lead Angular/React (Antigravity + GitHub MCP). Atomic Design, Signals, TailwindCSS.",
      "Bruno — QA Agent (GitHub Actions CI). Automático, sin intervención humana.",
    ],
    layer2Text:
      "Cada agente recibe contexto via CLAUDE.md en su repo, lee sus instrucciones via GitHub MCP, espera un issue number, y ejecuta dentro de guardrails explícitos. No improvisan — operan dentro de un sistema.",
    layer3Title: "3. GitHub MCP como sistema nervioso",
    layer3Text:
      "El GitHub MCP conectado a Claude es lo que hace que todo sea real y no solo conversación. Orion puede leer archivos, crear issues, hacer push de código, gestionar PRs — todo desde la sesión de Claude. No hay copia-pega manual entre el modelo y el repo. El estado del sistema vive en GitHub; las decisiones viven en GitHub; el trabajo de los agentes entra y sale de GitHub.",
    toolsTitle: "Las herramientas IA que usamos y para qué",
    tools: [
      {
        name: "Claude.ai Pro (Sonnet/Opus)",
        role: "Orion — arquitectura, decisiones, coordinación",
      },
      {
        name: "GitHub Copilot Pro (VSCode)",
        role: "Nestor — implementación backend día a día",
      },
      {
        name: "Antigravity",
        role: "Olga — implementación frontend, Angular/React",
      },
      {
        name: "Gemini Pro/Flash",
        role: "Tareas mecánicas, refactors, análisis rápido",
      },
      {
        name: "GitHub Actions",
        role: "Bruno — CI/CD, SonarCloud, QA automático",
      },
      {
        name: "SonarCloud",
        role: "Calidad de código — 3 capas (IDE → CI → deploy)",
      },
      {
        name: "GitHub MCP",
        role: "Conecta Claude con los repos en tiempo real",
      },
      { name: "Vercel MCP", role: "Deploy coordinado desde sesión" },
    ],
    toolsDesc:
      "El principio de selección de modelo está documentado como decisión (D66): modelo ligero para tareas mecánicas, modelo pesado para arquitectura. Nunca al revés — porque el coste está en el contexto, no en el token.",
    discoveriesTitle: "Lo nuevo que hemos descubierto sobre trabajar con IA",
    discoveries: [
      {
        name: "El contexto es el producto.",
        text: "Un modelo sin contexto estructurado es un asistente genérico. El mismo modelo con ORION.md + DECISIONS.md + gameon.md es un CTO técnico que recuerda 33 sesiones de decisiones y puede retomar exactamente donde lo dejamos.",
      },
      {
        name: "Los agentes necesitan guardrails, no libertad.",
        text: 'El descubrimiento más importante: los agentes IA ejecutan mejor cuando tienen restricciones explícitas. Nestor no "crea issues" — eso lo hace solo Orion (D5). Olga no mezcla Tailwind con valores hardcodeados — está en las reglas del agente. Cuando reduces el espacio de decisión del agente, la calidad de output sube.',
      },
      {
        name: "La calidad de un issue determina la calidad del output.",
        text: "Desarrollamos un sistema de quality score para issues (≥8/10 antes de asignar a un agente). Un issue mal escrito produce código que hay que rehacer. Un issue bien estructurado — contexto + prompt del agente + test plan — produce código que pasa CI al primer intento.",
      },
      {
        name: "Los MCP cambian el tipo de trabajo posible.",
        text: "Sin MCP, Claude es un modelo de texto que genera código para copiar. Con GitHub MCP, Claude puede leer el estado real del repo, crear el issue, hacer push de la solución, y abrir la PR — sin que tú toques el teclado para esas operaciones. Es un salto cualitativo, no cuantitativo.",
      },
      {
        name: "La separación sistema/instancia escala el conocimiento.",
        text: "En v2.0.0 extrajimos el sistema genérico a un repo público (orion-os) y mantuvimos la instancia privada (orion). Esto permite que el framework sea reusable mientras el conocimiento específico del proyecto permanece privado. Es la misma lógica que un framework open-source con proyectos privados encima.",
      },
    ],
    productionTitle: "Lo que hemos producido con este sistema",
    productionText1:
      "Con esta arquitectura, un solo desarrollador ha llevado GameOn desde cero hasta producción en ~33 sesiones: NestJS + PostgreSQL + JWT + Google OAuth en backend, Angular 21 standalone + Signals + Tailwind en frontend, CI/CD con GitHub Actions + SonarCloud quality gates, deploy en Vercel con staging separado, sistema de organizaciones, torneos, perfiles públicos, sistema de roles, y pipeline de migrations automatizado.",
    productionText2:
      "Un segundo proyecto (NutriApp — React + Supabase) corre en paralelo con el mismo sistema operativo, sin contaminar el contexto de GameOn.",
    productionHighlight:
      "Eso es Orion OS. No es un conjunto de prompts — es una manera de trabajar con IA que escala con la complejidad del producto.",
  },
};

function OrionOs() {
  const isEn = useSelector((state) => state.translation);
  const dispatch = useDispatch();
  const content = isEn ? data.en : data.es;
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLang = (targetLang) => {
    const shouldBeEn = targetLang === "en";
    if (isEn !== shouldBeEn) dispatch(translate());
  };

  return (
    <main className="orion-os">
      <div className="container">
        <div className="orion-os__header">
          <Link to="/" className="orion-os__back-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {content.back}
          </Link>
          <div className="orion-os__lang-toggle">
            <button
              type="button"
              className={`orion-os__lang-btn${!isEn ? " orion-os__lang-btn--active" : ""}`}
              onClick={() => handleLang("es")}
            >
              🇪🇸 ES
            </button>
            <button
              type="button"
              className={`orion-os__lang-btn${isEn ? " orion-os__lang-btn--active" : ""}`}
              onClick={() => handleLang("en")}
            >
              🇬🇧 EN
            </button>
          </div>
        </div>

        <div className="orion-os__content">
          <h1 className="orion-os__title">{content.title}</h1>
          <blockquote className="orion-os__quote">
            <p>{content.quote1}</p>
            <p>
              <strong>{content.quote2}</strong>
            </p>
          </blockquote>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.whatIsTitle}</h2>
            <p className="orion-os__text">{content.whatIsText1}</p>
            <p className="orion-os__text">{content.whatIsText2}</p>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.hierarchyTitle}</h2>
            <pre className="orion-os__code-block">
              <code>{content.hierarchyCode}</code>
            </pre>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.commandsTitle}</h2>
            <div className="orion-os__table-wrapper">
              <table className="orion-os__table">
                <thead>
                  <tr>
                    <th>Command</th>
                    <th>What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {content.commands.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <code>{c.cmd}</code>
                      </td>
                      <td>{c.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="orion-os__text orion-os__subtext">
              {content.commandsRef}
            </p>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.coreConceptsTitle}</h2>
            <ul className="orion-os__list">
              {content.coreConcepts.map((concept, i) => (
                <li key={i}>
                  <strong>{concept.name}</strong> — {concept.desc}
                </li>
              ))}
            </ul>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.repoTitle}</h2>
            <pre className="orion-os__code-block">
              <code>{content.repoCode}</code>
            </pre>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">
              {content.activeProjectsTitle}
            </h2>
            <div className="orion-os__table-wrapper">
              <table className="orion-os__table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Stack</th>
                    <th>Topology</th>
                    <th>Issues repo</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {content.activeProjects.map((p, i) => (
                    <tr key={i}>
                      <td>{p.name}</td>
                      <td>{p.stack}</td>
                      <td>{p.top}</td>
                      <td>{p.repo}</td>
                      <td>{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.roadmapTitle}</h2>
            <pre className="orion-os__code-block">
              <code>{content.roadmapCode}</code>
            </pre>
          </section>

          <hr className="orion-os__divider" />

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.paradigmTitle}</h2>
            <p className="orion-os__text">{content.paradigmText1}</p>
            <p className="orion-os__text">{content.paradigmText2}</p>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.layersTitle}</h2>
            <p className="orion-os__text">{content.layersText1}</p>
            <p className="orion-os__text">{content.layersText2}</p>

            <div className="orion-os__layer">
              <h3>{content.layer1Title}</h3>
              <p>{content.layer1Text}</p>
            </div>

            <div className="orion-os__layer">
              <h3>{content.layer2Title}</h3>
              <ul>
                {content.layer2List.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p>{content.layer2Text}</p>
            </div>

            <div className="orion-os__layer">
              <h3>{content.layer3Title}</h3>
              <p>{content.layer3Text}</p>
            </div>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.toolsTitle}</h2>
            <div className="orion-os__table-wrapper">
              <table className="orion-os__table">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {content.tools.map((t, i) => (
                    <tr key={i}>
                      <td>{t.name}</td>
                      <td>{t.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="orion-os__text">{content.toolsDesc}</p>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.discoveriesTitle}</h2>
            <ul className="orion-os__list">
              {content.discoveries.map((disc, i) => (
                <li key={i}>
                  <strong>{disc.name}</strong> {disc.text}
                </li>
              ))}
            </ul>
          </section>

          <section className="orion-os__section">
            <h2 className="orion-os__subtitle">{content.productionTitle}</h2>
            <p className="orion-os__text">{content.productionText1}</p>
            <p className="orion-os__text">{content.productionText2}</p>
            <p className="orion-os__text orion-os__highlight">
              <strong>{content.productionHighlight}</strong>
            </p>
          </section>
        </div>
      </div>

      {showScrollTop && (
        <button
          className="orion-os__scroll-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label={isEn ? "Go up" : "Subir"}
        >
          <i className="uil uil-arrow-up"></i>
          <span>{isEn ? "Go up" : "Subir"}</span>
        </button>
      )}
    </main>
  );
}

export default OrionOs;
